import { computeHalfTrimWidth } from '../helpers/page-trim-width'
import { PrintBleedTintOverlay, SinglePageSpineBleedStrip } from '../print-bleed'

type SinglePageBleedOverlayAboveShapesProps = {
  bleedWidth: number
  bleedHeight: number
  bleedMargin: number
  trimWidth: number
  trimHeight: number
  /** Same `side` as `SinglePageDocument` — which half of the spread is shown. */
  singlePageSide: 'left' | 'right'
  /**
   * The in-trim safety band/labels always render regardless of this flag; only the outer
   * bleed-edge band/labels are gated on it — fed from the editor's "Show Bleed Bar" toggle. This
   * component itself is therefore no longer conditionally mounted on that toggle; it always
   * renders so the safety band stays up.
   */
  showOutsideTrim: boolean
}

/**
 * Print bleed tint + labels above `ShapeManager` (z-6), matching
 * {@link DoubleSpreadBleedOverlayAboveShapes} for spread view. Single-page document no longer
 * embeds `PrintBleedTintOverlay` so shapes do not cover the yellow bands.
 *
 * `PrintBleedTints` with `centerGap === 0` does not paint the inner-yellow column at the spine
 * (only the pasteboard far left/right); {@link SinglePageSpineBleedStrip} restores that band.
 */
export function SinglePageBleedOverlayAboveShapes({
  bleedWidth,
  bleedHeight,
  bleedMargin,
  trimWidth,
  trimHeight,
  singlePageSide,
  showOutsideTrim,
}: SinglePageBleedOverlayAboveShapesProps) {
  const pageTrimWidth = computeHalfTrimWidth(trimWidth)

  if (pageTrimWidth <= 0) {
    return null
  }

  return (
    <div className="pointer-events-none absolute inset-0" style={{ zIndex: 6 }}>
      <PrintBleedTintOverlay
        bleedWidth={bleedWidth}
        bleedHeight={bleedHeight}
        bleedMargin={bleedMargin}
        trimWidth={trimWidth}
        trimHeight={trimHeight}
        visible
        isSinglePage
        singlePageSide={singlePageSide}
        zIndex={1}
        centerGap={0}
        showOutsideTrim={showOutsideTrim}
      />
      <SinglePageSpineBleedStrip
        bleedHeight={bleedHeight}
        bleedMargin={bleedMargin}
        trimHeight={trimHeight}
        trimWidth={trimWidth}
        singlePageSide={singlePageSide}
        zIndexBase={8}
        showOutsideTrim={showOutsideTrim}
      />
    </div>
  )
}
